import React from 'react'
import {defineMessages, FormattedMessage} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useRoomOfferInteraction} from 'hooks/roomOfferInteraction/useRoomOfferInteraction'
import {useGetIsOfferSoldOut} from 'hooks/useGetIsOfferSoldOut/useGetIsOfferSoldOut'
import {OfferAccessFlow} from 'modules/sapiSearch/constants'
import {
  getHotelApiRoomsParameters,
  getRoomOfferAccessConfig
} from 'modules/searchApi/selectors'

import {Button} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import mirror from '@vio/atlas/src/utils/mirror'
import {useNetworkStatus} from '@vio/core/src/_web/utils/network/hooks/useNetworkStatus'
import {Offer} from '@vio/core/src/offer/types/offer'
import {Room, SplitBooking} from '@vio/core/src/room/types/room'

type Props = {
  hotelId: string
  room?: Room
  offer?: Offer
  splitBooking?: SplitBooking
  component: string
}

const ChevronWrapper = styled.div`
  ${mirror()}
`

const ctaMessages = defineMessages({
  unlock: {
    id: 'signUpCTAUnlock',
    defaultMessage: 'Unlock'
  },
  appLocked: {
    id: 'appBanner.button',
    defaultMessage: 'Get the app'
  },
  appLockedExclusiveV2: {
    id: 'offer.appExclusiveCta',
    defaultMessage: 'View on app'
  },
  book: {
    id: 'book',
    defaultMessage: 'Book'
  },
  soldOut: {
    id: 'soldOut',
    defaultMessage: 'Sold out'
  }
})

const getIcon = ({isRoomOfferAppLocked}: {isRoomOfferAppLocked: boolean}) => {
  if (isRoomOfferAppLocked) return <Icon name="Iphone" />
  return null
}

export const RoomOfferRegularCTA = ({
  hotelId,
  room,
  offer,
  splitBooking,
  component
}: Props) => {
  const isOnline = useNetworkStatus()
  const isOfferSoldOut = useGetIsOfferSoldOut({offer, splitBooking})
  const flowType = useSelector(state =>
    getRoomOfferAccessConfig(state, offer?.id)
  )
  const isRoomOfferAppLocked = flowType === OfferAccessFlow.AppLocked
  const {bookingHint} = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const {handleClick} = useRoomOfferInteraction({
    hotelId,
    roomId: room?.id || splitBooking?.offers[0].room.id,
    room: splitBooking ? undefined : room,
    offer: {offer, splitBooking},
    component,
    hasBookingHint: bookingHint === '1'
  })

  const ctaMessage = () => {
    if (isOfferSoldOut) return ctaMessages.soldOut
    if (isRoomOfferAppLocked) return ctaMessages.appLockedExclusiveV2
    return ctaMessages.book
  }

  const shouldShowChevron = !isRoomOfferAppLocked && !isOfferSoldOut

  return (
    <Button
      onClick={handleClick}
      variant="primary"
      fullWidth
      iconStart={getIcon({isRoomOfferAppLocked})}
      iconEnd={
        shouldShowChevron && (
          <ChevronWrapper>
            <Icon name="ChevronRight" />
          </ChevronWrapper>
        )
      }
      dataId={
        flowType !== OfferAccessFlow.RegularDeal
          ? 'accommodationRoomLockedOfferCTA'
          : 'accommodationRoomOfferCTA'
      }
      disabled={isOfferSoldOut || !isOnline}
    >
      <FormattedMessage {...ctaMessage()} />
    </Button>
  )
}
